<script lang="ts">
	// UC-4 and UC-18. Where the order is going: the body of the delivery sheet,
	// one step at a time (T7). The till draws the sheet around it, with the
	// title, Back, the fee, "New customer" or "New address", and Confirm, from
	// the same steps (src/till/delivery.svelte.ts).
	//
	// The read-back names the driver the cart already has (UC-4.R6): the
	// sheet never asks for one, since Send does, in one tap (T33).
	//
	// Phone. The keypad types the number, and the number under the field is
	// the one the server will store, normalized by the same function. From
	// the fifth digit the saved customers whose number holds the digits are
	// offered, the digits set strong in each; a full number stays in the list
	// and still takes a tap, so the cashier always sees whom they picked.
	//
	// Where. The picked customer's addresses, the last one used picked. The
	// picked address shows in full above the list, with its landmark, area
	// and fee, because the cashier reads it back to the customer; the options
	// stay one line so picking never moves the list (Patterns, "read it
	// back"). An address used from another shop, or before this shop had
	// areas, asks for its area. A manager can open the customer to correct
	// them.
	//
	// New customer, or a new address: everything at once. A name, an address
	// and an area are required, the area only in a shop that has areas; the
	// landmark is optional.

	import Button from '../components/Button.svelte';
	import Choice from '../components/Choice.svelte';
	import Field from '../components/Field.svelte';
	import Icon from '../components/Icon.svelte';
	import Keypad from '../components/Keypad.svelte';
	import NameTile from '../components/NameTile.svelte';
	import { fill, message, type Language } from '../i18n/messages';
	import { formatMinor } from '../format/money';
	import { knownCustomer, SUGGEST_FROM, type Suggestion } from '../till/customers.svelte';
	import {
		deletePhone,
		deliverySteps,
		deliverySuggestions,
		normalizedOrEmpty,
		PHONE_LENGTH,
		pickAddress,
		pickAddressArea,
		pickedPhone,
		pickSuggestion,
		setAddressForm,
		stepContext,
		typePhone,
	} from '../till/delivery.svelte';
	import { addressArea, addressChoice, canStartNew, pickedAddress } from '../till/deliverySteps';
	import type { components } from '../api/types';

	type Area = components['schemas']['AreaBody'];

	interface Props {
		language: Language;
		areas: Area[];
		minorDigits: number;
		countryCode: string;
		/** The shop's own fee, in a shop with no areas, once the quote says. */
		shopFeeMinor: number | null;
		/** A manager is signed in, so a saved customer can be corrected. */
		canEditCustomer: boolean;
		/** The till's store answers and reaches Bosla; a correction needs the internet. */
		online: boolean;
		onEditCustomer: () => void;
		/** The driver the cart names, or '' (UC-4.R6): Send asks for one when none is. */
		driverName: string;
	}

	let {
		language,
		areas,
		minorDigits,
		countryCode,
		shopFeeMinor,
		canEditCustomer,
		online,
		onEditCustomer,
		driverName,
	}: Props = $props();

	// A local number this long that still does not read is a mistake, not a
	// number half typed.
	const FULL_LENGTH = 11;

	const steps = $derived(deliverySteps());
	const phone = $derived(steps.phone);
	const normalized = $derived(normalizedOrEmpty(phone, countryCode));
	const areaIds = $derived(areas.map((area) => area.id));
	const context = $derived(stepContext(areaIds, countryCode));
	const suggested = $derived(deliverySuggestions());
	const customer = $derived(steps.customer);
	// The saved customer a manager may correct: only one the server holds,
	// with its id, and only with the internet (#972; a save the till kept
	// offline has no id until the server's list comes).
	const editable = $derived(knownCustomer(pickedPhone()));

	function areaOf(areaId: string | undefined): Area | undefined {
		return areaId ? areas.find((area) => area.id === areaId) : undefined;
	}

	const phoneError = $derived(
		!normalized && phone.length >= FULL_LENGTH
			? message(language, 'deliveryPhoneCheck')
			: undefined,
	);
	const noMatch = $derived(
		phone.length >= SUGGEST_FROM && suggested.digits === phone && suggested.list.length === 0,
	);

	function detailOf(suggestion: Suggestion): string {
		const last = suggestion.address;
		if (!last) return message(language, 'deliveryNoSavedAddress');
		return [last.address, areaOf(last.areaId)?.name].filter(Boolean).join(' · ');
	}

	const areaOptions = $derived(
		areas.map((area) => ({
			value: area.id,
			label: area.name,
			price: formatMinor(area.feeMinor, minorDigits),
			testId: `area-${area.id}`,
		})),
	);

	const savedOptions = $derived(
		(customer?.addresses ?? []).map((address) => {
			const area = areaOf(address.areaId);
			return {
				value: addressChoice(address),
				label: address.address,
				sub: [address.landmark, area?.name].filter(Boolean).join(' · ') || undefined,
				price: area ? formatMinor(area.feeMinor, minorDigits) : undefined,
				testId: `saved-address-${addressChoice(address)}`,
			};
		}),
	);
	const picked = $derived(pickedAddress(steps));
	const pickNeedsArea = $derived(!!picked && areaIds.length > 0 && !addressArea(picked, areaIds));
	// The area and fee the picked address comes to: its own area, or the one
	// picked for it; in a shop with no areas, the shop's fee.
	const pickedArea = $derived(
		picked ? areaOf(addressArea(picked, areaIds) || steps.pickArea) : undefined,
	);
	const readBackFee = $derived(
		areaIds.length === 0
			? shopFeeMinor === null
				? undefined
				: formatMinor(shopFeeMinor, minorDigits)
			: pickedArea
				? formatMinor(pickedArea.feeMinor, minorDigits)
				: undefined,
	);
	const showForm = $derived(
		steps.step === 'new' || (steps.step === 'where' && (steps.addrNew || !customer)),
	);
	const required = $derived(message(language, 'deliveryRequired'));
</script>

{#snippet normalizedNote()}
	<bdi dir="ltr">{normalized}</bdi>
{/snippet}

{#snippet areaChoice(value: string, onChange: (areaId: string) => void)}
	<Choice
		testId="delivery-areas"
		title={message(language, 'deliveryArea')}
		rule={required}
		options={areaOptions}
		{value}
		onChange={(areaId) => onChange(String(areaId))}
	/>
{/snippet}

{#snippet addressFields()}
	<Field
		testId="delivery-address"
		label={message(language, 'deliveryAddress')}
		value={steps.form.address}
		maxLength={500}
		placeholder={message(language, 'deliveryAddressHint')}
		note={steps.form.address.trim() ? undefined : required}
		onInput={(address) => setAddressForm({ address })}
	/>
	<Field
		testId="delivery-landmark"
		label={message(language, 'deliveryLandmark')}
		value={steps.form.landmark}
		maxLength={200}
		placeholder={message(language, 'deliveryLandmarkHint')}
		onInput={(landmark) => setAddressForm({ landmark })}
	/>
{/snippet}

{#snippet form()}
	<div class="split">
		<div class="column" data-scroll="">
			{#if steps.step === 'new'}
				<Field
					testId="delivery-phone"
					label={message(language, 'deliveryPhone')}
					value={phone}
					mono
				/>
				<Field
					testId="delivery-name"
					label={message(language, 'deliveryName')}
					value={steps.form.name}
					maxLength={120}
					note={steps.form.name.trim() ? undefined : required}
					onInput={(name) => setAddressForm({ name })}
				/>
			{/if}
			{@render addressFields()}
		</div>
		{#if areas.length > 0}
			<div class="column" data-scroll="">
				{@render areaChoice(steps.form.areaId, (areaId) => setAddressForm({ areaId }))}
			</div>
		{/if}
	</div>
{/snippet}

<div class="delivery" data-testid="panel-delivery">
	{#if steps.step === 'phone'}
		<div class="split">
			<div class="column tight" data-scroll="">
				<Field
					testId="delivery-phone"
					label={message(language, 'deliveryPhone')}
					value={phone || '—'}
					mono
					error={phoneError}
					errorTestId="delivery-phone-invalid"
					note={normalized
						? normalizedNote
						: phone.length < SUGGEST_FROM
							? message(language, 'deliverySuggestFrom')
							: undefined}
					noteTestId={normalized ? 'delivery-phone-normalized' : 'delivery-suggest-from'}
				/>
				{#if noMatch}
					<p class="quiet" data-testid="delivery-no-match">
						{canStartNew(context)
							? message(language, 'deliveryNewNumber')
							: message(language, 'deliveryNoMatch')}
					</p>
				{/if}
				{#if suggested.list.length > 0}
					<ul
						class="suggestions"
						role="list"
						aria-label={message(language, 'deliverySuggestions')}
						data-testid="delivery-suggestions"
					>
						{#each suggested.list as suggestion (suggestion.id)}
							<NameTile
								testId="delivery-suggest-{suggestion.id}"
								name={suggestion.name}
								phone={suggestion.national}
								detail={detailOf(suggestion)}
								match={suggested.digits}
								onPick={() => pickSuggestion(suggestion, countryCode)}
							/>
						{/each}
					</ul>
				{/if}
			</div>
			<div class="pad">
				<div class="fit">
					<Keypad
						testId="delivery-pad"
						fit
						label={message(language, 'deliveryPhone')}
						deleteLabel={message(language, 'backspace')}
						canType={phone.length < PHONE_LENGTH}
						canDelete={phone.length > 0}
						onKey={(key) => typePhone(key, countryCode)}
						onBackspace={() => deletePhone(countryCode)}
					/>
				</div>
			</div>
		</div>
	{:else}
		{#if steps.step === 'where' && customer}
			<div class="who">
				<Icon name="user" size={20} />
				<span class="name" data-testid="delivery-customer">{customer.name}</span>
				<bdi class="number" dir="ltr">{phone}</bdi>
				<span class="spacer"></span>
				{#if canEditCustomer && editable}
					<Button
						testId="customer-edit"
						label={message(language, 'customerEdit')}
						icon="edit"
						tone="secondary"
						size="sm"
						disabled={!online || !editable.id}
						onPress={onEditCustomer}
					/>
				{/if}
			</div>
			{#if canEditCustomer && editable && (!online || !editable.id)}
				<p class="hint offline" data-testid="customer-edit-internet">
					{message(language, 'customerEditNeedsInternet')}
				</p>
			{/if}
		{/if}
		{#if showForm}
			{@render form()}
		{:else}
			{#if picked}
				<div class="readback" role="status" aria-live="polite" data-testid="delivery-readback">
					<Icon name="pin" size={22} />
					<div class="said">
						<span class="hint">{message(language, 'deliveryReadBack')}</span>
						<span class="where" data-testid="delivery-readback-address">{picked.address}</span>
						{#if picked.landmark}
							<span class="landmark" data-testid="delivery-readback-landmark"
								>{picked.landmark}</span
							>
						{/if}
						<div class="area">
							{#if areaIds.length > 0 && pickedArea}
								<span class="known" data-testid="delivery-readback-area">{pickedArea.name}</span>
							{:else if areaIds.length > 0}
								<span class="unknown">{message(language, 'deliveryNoAreaYet')}</span>
							{/if}
							{#if readBackFee !== undefined}
								<bdi class="fee" dir="ltr" data-testid="delivery-readback-fee">{readBackFee}</bdi>
							{/if}
						</div>
						{#if driverName}
							<span class="hint" data-testid="delivery-readback-driver"
								>{fill(message(language, 'driverLabel'), driverName)}</span
							>
						{/if}
					</div>
				</div>
			{/if}
			<div class="column loose" data-scroll="">
				<Choice
					testId="saved-addresses"
					title={message(language, 'customerSaved')}
					options={savedOptions}
					value={picked ? addressChoice(picked) : ''}
					onChange={(choice) => pickAddress(String(choice))}
				/>
				{#if pickNeedsArea}
					{@render areaChoice(steps.pickArea, pickAddressArea)}
				{/if}
			</div>
		{/if}
	{/if}
</div>

<style>
	.delivery {
		flex: 1;
		min-block-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.split {
		flex: 1;
		min-block-size: 0;
		display: flex;
		gap: var(--space-9);
	}

	.column {
		flex: 1 1 280px;
		min-inline-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.column.tight {
		gap: var(--space-5);
	}

	.column.loose {
		flex: 1;
		min-block-size: 0;
		gap: var(--space-9);
	}

	.quiet {
		margin: 0;
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.suggestions {
		margin: 0;
		padding: 0;
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		gap: var(--touch-gap);
	}

	.pad {
		flex: none;
		inline-size: calc(var(--key-w) * 3 + var(--touch-gap) * 2);
		min-block-size: 0;
		overflow: hidden;
		display: flex;
		flex-direction: column;
	}

	.fit {
		flex: 1 1 0;
		min-block-size: 0;
	}

	.who {
		flex: none;
		display: flex;
		align-items: center;
		gap: var(--space-4);
		flex-wrap: wrap;
	}

	.name {
		font-size: var(--text-base);
		font-weight: 700;
	}

	.number {
		font-family: var(--font-mono);
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	.spacer {
		flex: 1;
	}

	.readback {
		flex: none;
		display: flex;
		align-items: flex-start;
		gap: var(--space-5);
		padding: var(--space-5) var(--space-7);
		background: var(--surface-card);
		border: var(--border-width-strong) solid var(--text-strong);
		border-radius: var(--radius-card);
	}

	.readback > :global(svg) {
		margin-block-start: var(--space-1);
	}

	.said {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.hint {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.hint.offline {
		margin: calc(-1 * var(--space-2)) 0 0;
		text-align: end;
	}

	.where {
		font-size: var(--text-md);
		font-weight: 700;
		line-height: var(--leading-snug);
		color: var(--text-strong);
		text-wrap: pretty;
	}

	.landmark {
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.area {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: var(--space-2) var(--space-5);
		font-size: var(--text-sm);
		font-weight: 600;
	}

	.known {
		color: var(--text-strong);
	}

	.unknown {
		color: var(--text-muted);
	}

	.fee {
		font-family: var(--font-mono);
		color: var(--text-strong);
	}
</style>
